import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Flame, Video, Scissors, Share2, Link, DollarSign, ArrowRight, ChevronRight, Clock, ShieldCheck, Wallet, HelpCircle, Coins, Lock, Crown, Handshake, ExternalLink, LibraryBig, ClipboardCheck, Eye } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import Layout from '@/components/Layout';
import CampaignBudgetBar from '@/components/CampaignBudgetBar';
import { RankingCampaignSummary } from '@/components/campaigns/RankingCampaignSummary';
import { EndedCampaignsSummary } from '@/components/campaigns/EndedCampaignsSummary';
import { CollapsibleCpmLeaderboard } from '@/components/cpm/CollapsibleCpmLeaderboard';
import { CurrentSeasonHall } from '@/components/ranks/CurrentSeasonHall';
import { CampaignWalletCard } from '@/components/CampaignWalletCard';
import { CampaignBanner } from '@/components/campaigns/CampaignBanner';
import { CampaignCategoryBadges } from '@/components/campaigns/CampaignCategoryBadges';
import { CampaignBannerInfo } from '@/components/campaigns/CampaignBannerInfo';
import { campaignInitials, resolveCampaignAvatar } from '@/lib/campaigns/resolveCampaignAvatar';
import { formatCents, formatDateTime } from '@/lib/format';
import { parseRequiredHashtags } from '@/lib/parseRequiredHashtags';
import { useEarnPageCampaignsQuery } from '@/generated/graphql';
import type { MyCpmCampaign, MyYoutubeChannel } from '@/generated/graphql';
import { EARN_PAGE_ENDED_CAMPAIGNS, EARN_PAGE_SCHEDULED_CAMPAIGNS } from '@/queries/queries';
import { useHasFeature } from '@/lib/useHasFeature';
import { useIsPaidSubscriber } from '@/lib/quota/useIsPaidSubscriber';
import { deriveCampaignAccess } from '@/lib/campaigns/deriveCampaignAccess';
import { isCampaignBudgetExhausted } from '@/lib/campaigns/isCampaignBudgetExhausted';
import { hasScheduledStartDate } from '@/lib/campaigns/hasScheduledStartDate';
import { isProExclusiveCampaign } from '@/lib/campaigns/isProExclusiveCampaign';
import { splitEarnPageCampaigns } from '@/lib/campaigns/splitEarnPageCampaigns';
import { describeMinViewsRequirement } from '@/lib/campaigns/describeMinViewsRequirement';
import { campaignDistributedCents } from '@/lib/campaigns/summarizeEndedCampaigns';
import type { CampaignCompetitionFields } from '@/lib/campaigns/rankingCompetition';
import { SiTiktok, SiInstagram, SiYoutube } from 'react-icons/si';
import { ClipperFeatureNagModal } from '@/components/ClipperFeatureNagModal';
import { useClipperFeatureNag } from '@/hooks/use-clipper-feature-nag';

// EARN_PAGE_ENDED_CAMPAIGNS only selects a subset of MyCpmCampaign — typed here
// by hand (rather than via generated hook) because codegen needs GraphQL
// introspection, which production disables. Fields mirror the query exactly.
type EndedCampaign = Pick<
  MyCpmCampaign,
  'id' | 'name' | 'logo_url' | 'banner_url' | 'categories' | 'currency' | 'budget_total_cents' | 'budget_remaining_cents'
> & CampaignCompetitionFields & {
  youtube_channels: Pick<MyYoutubeChannel, 'channel_id' | 'name' | 'thumbnail'>[];
};

type EarnPageEndedCampaignsQueryResult = {
  myCpmCampaigns: EndedCampaign[];
};

// EARN_PAGE_SCHEDULED_CAMPAIGNS — same "hand-typed subset" reasoning as above.
type ScheduledCampaign = Pick<
  MyCpmCampaign,
  'id' | 'name' | 'logo_url' | 'banner_url' | 'categories' | 'currency' | 'cpm_rate_cents' | 'start_date' | 'budget_total_cents'
> & CampaignCompetitionFields & {
  youtube_channels: Pick<MyYoutubeChannel, 'channel_id' | 'name' | 'thumbnail'>[];
};

type EarnPageScheduledCampaignsQueryResult = {
  myCpmCampaigns: ScheduledCampaign[];
};

// ---------------------------------------------------------------------------
// PROTOTYPE DATA — replace with API call once Nihey implements backend.
// Backend shape expected:
//   GET /api/partners  → Partner[]
//   Partner fields: id, name, logo_url, tagline, link_type, external_url, channel_id, active
// ---------------------------------------------------------------------------
type PartnerLinkType = 'external' | 'acervo';

interface Partner {
  id: string;
  name: string;
  logo_url: string | null;
  tagline: string;
  link_type: PartnerLinkType;
  external_url: string | null;  // 'external' type: opens partner site in new tab
  channel_id: string | null;    // 'acervo' type: routes to /community-library?channelId=
}

// Vitrine de Parcerias temporarily hidden until the partner links are
// confirmed (Networking Club + Trexx). Re-enable by pointing MOCK_PARTNERS
// back at MOCK_PARTNERS_PENDING once both external_url values are verified.
const MOCK_PARTNERS_PENDING: Partner[] = [
  {
    id: 'networking-club',
    name: 'Networking Club',
    logo_url: null,
    tagline: 'Pode ter campanhas e benefícios exclusivos esperando por você. Acesse o site do Networking Club e confira.',
    link_type: 'external',
    external_url: 'https://www.networkingclub.pro/', // TODO: confirmar URL real do Networking Club
    channel_id: null,
  },
  {
    id: 'trexx',
    name: 'Trexx',
    logo_url: null,
    tagline: 'Pode ter campanhas e acervos exclusivos esperando por você. Acesse o site da Trexx e confira.',
    link_type: 'external',
    external_url: 'https://trexx.com.br/', // TODO: confirmar URL real da Trexx
    channel_id: null,
  },
];

// Flip to true once both partner links above are confirmed.
const PARTNERS_ENABLED: boolean = false;
const MOCK_PARTNERS: Partner[] = PARTNERS_ENABLED ? MOCK_PARTNERS_PENDING : [];

const STEPS = [
  {
    number: 1,
    icon: Video,
    titleKey: 'earnPage.step1Title',
    descKey: 'earnPage.step1Desc',
  },
  {
    number: 2,
    icon: Scissors,
    titleKey: 'earnPage.step2Title',
    descKey: 'earnPage.step2Desc',
  },
  {
    number: 3,
    icon: Share2,
    titleKey: 'earnPage.step3Title',
    descKey: 'earnPage.step3Desc',
  },
  {
    number: 4,
    icon: Link,
    titleKey: 'earnPage.step4Title',
    descKey: 'earnPage.step4Desc',
  },
  {
    number: 5,
    icon: DollarSign,
    titleKey: 'earnPage.step5Title',
    descKey: 'earnPage.step5Desc',
  },
];

const FAQ_KEYS = ['q1', 'q2', 'q3', 'q4', 'q5'] as const;

// Campaign card grids (joined, open, partner, loading). Four across on wide
// screens keeps the cards compact.
const CAMPAIGN_GRID_CLASS = 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3';

const LEFT_PARTICLES = [
  { Icon: Video,    delay: 0,   left: '8%'  },
  { Icon: Scissors, delay: 1.5, left: '30%' },
  { Icon: Share2,   delay: 2.8, left: '56%' },
  { Icon: Video,    delay: 0.9, left: '78%' },
];

const RIGHT_PARTICLES = [
  { Icon: DollarSign, delay: 0.5, left: '12%' },
  { Icon: Coins,      delay: 1.9, left: '40%' },
  { Icon: DollarSign, delay: 3.1, left: '66%' },
  { Icon: Coins,      delay: 1.2, left: '85%' },
];

const EarnPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const faqRef = useRef<HTMLDivElement>(null);
  const { open: isClipperFeatureNagOpen, onContinue: handleClipperFeatureNagContinue } =
    useClipperFeatureNag('earn');
  const { data, loading } = useEarnPageCampaignsQuery({ fetchPolicy: 'no-cache', pollInterval: 60_000, context: { queryDeduplication: false } });
  const { data: endedData, loading: endedLoading } = useQuery<EarnPageEndedCampaignsQueryResult>(EARN_PAGE_ENDED_CAMPAIGNS);
  const { data: scheduledData } = useQuery<EarnPageScheduledCampaignsQueryResult>(EARN_PAGE_SCHEDULED_CAMPAIGNS);
  const isProUser = useHasFeature('pro_features');
  // Widened from Pro+-only to any paid plan on 03/08/2026 (CEO request) — see
  // deriveCampaignAccess.ts docstring for the backend caveat.
  const isPaidUser = useIsPaidSubscriber();

  const campaigns = data?.myCpmCampaigns ?? [];
  const activeCampaignCount = campaigns.length;

  // A joined campaign is lifted into its own section, so it must not also
  // render among the open or partner cards below.
  const {
    joined: joinedCampaigns,
    internal: internalCampaigns,
    partner: partnerCampaigns,
  } = splitEarnPageCampaigns(campaigns);

  const endedCampaigns = endedData?.myCpmCampaigns ?? [];
  const scheduledCampaigns = scheduledData?.myCpmCampaigns ?? [];

  const renderCard = (campaign: (typeof campaigns)[0]) => {
    const primaryChannel = campaign.youtube_channels?.[0] ?? null;
    // Branded after the campaign (name + optional campaign logo), falling back
    // to the channel equivalent — preserves main's campaign-logo branding while
    // keeping the PR's PRO/invite gating + social icons.
    const channelName = campaign.name || primaryChannel?.name || '';
    const channelThumbnail = resolveCampaignAvatar(campaign).src;
    const rateLabel = formatRate(campaign.cpm_rate_cents, campaign.currency);
    const minViews = describeMinViewsRequirement(campaign.min_views_threshold);
    const remainingLabel = formatCents(campaign.budget_remaining_cents, campaign.currency);
    const spentCents = Math.max(0, campaign.budget_total_cents - campaign.budget_remaining_cents);
    const budgetExhausted = isCampaignBudgetExhausted({
      remainingCents: campaign.budget_remaining_cents,
      totalCents: campaign.budget_total_cents,
    });
    const isPartnerCampaign = campaign.youtube_channels.some(ch => ch.is_autoclipper_partner);
    const access = deriveCampaignAccess({
      isJoined: !!campaign.is_joined,
      joinBlockedReason: campaign.join_blocked_reason,
      inviteOnly: !!campaign.invite_only,
      approvedPlatformCount: (campaign.my_approved_platforms ?? []).length,
      isPartnerCampaign,
      isProUser,
      isPaidUser,
      isSelectedMode: !!campaign.selected_only,
      isSelectedUser: !!campaign.is_selected_user,
    });
    const isProGated = access === 'pro_locked';
    // PRO-locked cards hide the brand ("Parceiro" + lock), so they must not
    // reveal it through the banner or categories either.
    const bannerUrl = isProGated ? null : campaign.banner_url;
    // A selected-only campaign the viewer isn't on the list for locks the same
    // way an invite-only one does — the card renders, the CTA doesn't.
    const isLocked = access === 'invite_locked' || access === 'selected_locked';
    const isSelectedLocked = access === 'selected_locked';
    const isFreeLimitLocked = access === 'free_limit_locked';
    // Invite-only campaign the viewer can access because they have any paid
    // plan — mark it so they know it's a paid-plan perk, not a regular open
    // campaign.
    const isProExclusive = isProExclusiveCampaign({
      inviteOnly: !!campaign.invite_only,
      isPaidUser,
    });
    const platforms = parseRequiredHashtags(campaign.required_hashtags);
    const activePlatforms = [
      platforms.tiktok ? 'tiktok' : null,
      platforms.instagram ? 'instagram' : null,
      platforms.youtube ? 'youtube' : null,
    ].filter(Boolean) as string[];

    return (
      <Card
        key={campaign.id}
        className={
          isProGated || isLocked || isFreeLimitLocked
            ? 'group/card relative overflow-hidden h-full flex flex-col gap-2.5 p-3 border-border/40 bg-card opacity-90'
            : 'group/card relative overflow-hidden h-full flex flex-col gap-2.5 p-3 border-emerald-500/30 bg-gradient-to-b from-emerald-500/5 via-card to-card hover:shadow-[0_0_20px_rgba(16,185,129,0.15)] transition-shadow'
        }
      >
        {/* Categories and platforms ride on the banner (shorter card); the
            in-body copies below hide while a banner is shown, via :has(). */}
        <CampaignBanner src={bannerUrl} grayscale={isLocked} className="-mx-3 -mt-3 aspect-[2/1]">
          <CampaignBannerInfo
            categories={campaign.categories}
            platforms={activePlatforms}
            platformsTestId={`earn-card-banner-platforms-${campaign.id}`}
          />
        </CampaignBanner>

        {!isProGated && !isLocked && !isFreeLimitLocked && !bannerUrl && (
          <img
            src="/clip-pix-logo-white.png"
            alt=""
            aria-hidden="true"
            className="absolute top-4 right-4 w-20 h-20 object-contain opacity-[0.04] pointer-events-none select-none"
          />
        )}

        <div className="flex items-center gap-3">
          <div className="relative flex-shrink-0">
            {isProGated ? (
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-muted border-2 border-border/40">
                <Lock className="w-5 h-5 text-muted-foreground" />
              </div>
            ) : channelThumbnail ? (
              <img
                src={channelThumbnail}
                alt={channelName}
                className={`w-10 h-10 rounded-full object-cover border-2 ${isLocked ? 'border-border/40 grayscale' : 'border-emerald-500/40'}`}
              />
            ) : (
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold border-2 ${isLocked ? 'bg-muted border-border/40 text-muted-foreground' : 'bg-primary/20 border-primary/30 text-primary'}`}>
                {campaignInitials(channelName)}
              </div>
            )}
            {isLocked && (
              <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-5 h-5 bg-muted-foreground/20 border border-border/60 rounded-full">
                <Lock className="w-2.5 h-2.5 text-muted-foreground" />
              </span>
            )}
          </div>
          <div className="min-w-0">
            {isProGated ? (
              <>
                <div className="flex items-center gap-1.5">
                  <p className="font-bold text-sm truncate text-muted-foreground">
                    {t('earnPage.proLocked.partnerName')}
                  </p>
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-primary/15 text-primary border border-primary/30 flex-shrink-0">
                    PRO
                  </span>
                </div>
                {campaign.campaign_type !== 'ranking' && <p className="text-xs font-bold text-muted-foreground">
                  {t('earnPage.earnPerView')}
                </p>}
              </>
            ) : (
              <>
                <div className="flex items-center gap-1.5">
                  <p className="font-bold text-sm truncate">{campaign.name}</p>
                  {isProExclusive && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          data-testid="campaign-pro-exclusive-badge"
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-primary/15 text-primary border border-primary/30 flex-shrink-0 cursor-default"
                        >
                          <Crown className="w-2.5 h-2.5" />
                          {t('earnPage.proExclusive.badge')}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-[200px] text-center text-xs">
                        {t('earnPage.proExclusive.tooltip')}
                      </TooltipContent>
                    </Tooltip>
                  )}
                  {access === 'joined' && (
                    <span
                      data-testid="campaign-joined-badge"
                      className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-500/15 text-emerald-500 border border-emerald-500/30 flex-shrink-0"
                    >
                      {t('earnPage.joinedBadge')}
                    </span>
                  )}
                </div>
                {campaign.campaign_type !== 'ranking' && <Tooltip>
                  <TooltipTrigger asChild>
                    <p className={`text-xs font-bold cursor-default ${isLocked ? 'text-muted-foreground' : 'text-primary'}`}>
                      {t('earnPage.earnPerView')}
                    </p>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[200px] text-center text-xs">
                    {t('earnPage.cpmTooltip')}
                  </TooltipContent>
                </Tooltip>}
              </>
            )}
          </div>
        </div>

        {!isProGated && (
          <div
            data-testid={`earn-card-body-categories-${campaign.id}`}
            className="-mt-1 empty:hidden group-has-[[data-campaign-banner]]/card:hidden"
          >
            <CampaignCategoryBadges categories={campaign.categories} muted={isLocked} compact />
          </div>
        )}

        {campaign.campaign_type === 'ranking' ? <RankingCampaignSummary config={campaign.ranking_config} currency={campaign.currency} /> : <div>
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">
            {t('earnPage.payUpTo')}
          </p>
          <p className={`text-xl font-extrabold leading-none ${isLocked ? 'text-muted-foreground' : 'text-primary'}`}>
            {rateLabel}
            <span className="text-sm font-normal text-muted-foreground ml-1">
              {t('earnPage.perThousandViews')}
            </span>
            {activePlatforms.length > 0 && (
              <span
                data-testid={`earn-card-body-platforms-${campaign.id}`}
                className="inline-flex items-center gap-1 ml-2 align-middle group-has-[[data-campaign-banner]]/card:hidden"
              >
                {activePlatforms.includes('tiktok') && <SiTiktok className="w-3.5 h-3.5 text-muted-foreground" />}
                {activePlatforms.includes('instagram') && <SiInstagram className="w-3.5 h-3.5 text-muted-foreground" />}
                {activePlatforms.includes('youtube') && <SiYoutube className="w-3.5 h-3.5 text-muted-foreground" />}
              </span>
            )}
          </p>
          {minViews.kind === 'minimum' ? (
            <p
              data-testid={`earn-card-min-views-${campaign.id}`}
              className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-400/90"
            >
              <Eye className="w-3 h-3" />
              {t('earnPage.minViewsPerClip', { views: minViews.views.toLocaleString() })}
            </p>
          ) : (
            <p
              data-testid={`earn-card-no-min-views-${campaign.id}`}
              className={`mt-1 inline-flex items-center gap-1 text-[11px] ${isLocked ? 'text-muted-foreground' : 'text-emerald-500'}`}
            >
              <Eye className="w-3 h-3" />
              {t('earnPage.noMinViewsPerClip')}
            </p>
          )}
        </div>}

        {campaign.campaign_type !== 'ranking' && <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">{t('earnPage.remainingBudget')}</span>
            <span className={`font-semibold ${budgetExhausted ? 'text-red-400' : 'text-foreground'}`}>
              {budgetExhausted ? t('earnPage.budgetExhausted') : remainingLabel}
            </span>
          </div>
          <CampaignBudgetBar
            totalCents={campaign.budget_total_cents}
            spentCents={spentCents}
            pendingCents={campaign.budget_pending_cents}
            currency={campaign.currency}
            size="sm"
          />
        </div>}

        <div className="mt-auto space-y-2">
          {isProGated ? (
            <Button asChild size="sm" variant="outline" className="w-full font-bold border-primary/30 text-primary gap-2 hover:bg-primary/10 hover:text-primary">
              <RouterLink to="/plans">
                <Crown className="w-4 h-4" />
                {t('earnPage.proLocked.ctaLabel')}
              </RouterLink>
            </Button>
          ) : isFreeLimitLocked ? (
            <Button asChild size="sm" variant="outline" className="w-full font-bold border-primary/30 text-primary gap-2 hover:bg-primary/10 hover:text-primary">
              <RouterLink to="/plans">
                <Crown className="w-4 h-4" />
                {t('earnPage.freeLimit.cta')}
              </RouterLink>
            </Button>
          ) : isSelectedLocked ? (
            // No upgrade CTA here on purpose: a paid plan does NOT unlock a
            // selected-only campaign, so sending the viewer to /plans would be
            // selling them something that cannot grant access.
            <Button size="sm" variant="outline" disabled className="w-full font-bold gap-2">
              <Lock className="w-4 h-4" />
              {t('earnPage.selectedOnly.ctaLabel')}
            </Button>
          ) : isLocked ? (
            <Button asChild size="sm" variant="outline" className="w-full font-bold border-primary/30 text-primary gap-2 hover:bg-primary/10 hover:text-primary">
              <RouterLink to="/plans">
                <Crown className="w-4 h-4" />
                {t('earnPage.inviteOnly.ctaLabel')}
              </RouterLink>
            </Button>
          ) : (
            <Button
              variant="gradient"
              size="sm"
              className="w-full font-bold"
              onClick={() => navigate(`/campaigns/${campaign.id}`)}
            >
              {t('earnPage.earnWithCampaign')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
          {isFreeLimitLocked && (
            <p className="text-[11px] text-muted-foreground text-center">
              {t('earnPage.freeLimit.note')}
            </p>
          )}
        </div>
      </Card>
    );
  };

  const formatRate = (cpmRateCents: number, currency: string) => {
    return new Intl.NumberFormat(currency === 'USD' ? 'en-US' : 'pt-BR', {
      style: 'currency',
      currency,
    }).format(cpmRateCents / 100);
  };

  const renderEndedCard = (campaign: EndedCampaign) => {
    const primaryChannel = campaign.youtube_channels?.[0] ?? null;
    const channelName = campaign.name || primaryChannel?.name || '';
    const channelThumbnail = resolveCampaignAvatar(campaign).src;
    const paidCents = campaignDistributedCents(campaign);
    const paidLabel = formatCents(paidCents, campaign.currency);

    const paidPct = campaign.budget_total_cents > 0
      ? Math.min(100, (paidCents / campaign.budget_total_cents) * 100)
      : 100;

    return (
      <Card
        key={campaign.id}
        className="group/card relative overflow-hidden flex flex-col gap-3 p-4 border-border/40 bg-card/60"
      >
        <CampaignBanner src={campaign.banner_url} grayscale className="-mx-4 -mt-4 aspect-[2/1]">
          <CampaignBannerInfo categories={campaign.categories} />
        </CampaignBanner>

        <span className="inline-flex items-center gap-1.5 w-fit px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-[10px] font-bold uppercase tracking-wider">
          <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60" />
          {t('earnPage.endedBadge')}
        </span>

        <div className="flex items-start gap-3 min-w-0">
          {channelThumbnail ? (
            <img
              src={channelThumbnail}
              alt={channelName}
              className="w-10 h-10 rounded-full object-cover border-2 border-border/40 grayscale flex-shrink-0"
            />
          ) : (
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold border-2 bg-muted border-border/40 text-muted-foreground flex-shrink-0">
              {campaignInitials(channelName)}
            </div>
          )}
          <p className="font-bold text-sm leading-snug pt-1.5">{campaign.name}</p>
        </div>

        <div className="empty:hidden group-has-[[data-campaign-banner]]/card:hidden">
          <CampaignCategoryBadges categories={campaign.categories} muted compact />
        </div>

        {campaign.campaign_type === 'ranking' ? <><RankingCampaignSummary config={campaign.ranking_config} currency={campaign.currency} /><Button asChild variant="outline" className="mt-auto"><RouterLink to={`/campaigns/${campaign.id}`}>{t('ranking.viewCompetition')}</RouterLink></Button></> : <><div className="mt-auto h-1.5 w-full rounded-full bg-muted overflow-hidden">
          <div
            className="h-full rounded-full bg-primary/40"
            style={{ width: `${paidPct}%` }}
          />
        </div>

        <div className="flex items-baseline justify-between">
          <span className="text-lg font-extrabold text-primary/40">{paidLabel}</span>
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{t('earnPage.paidLabel')}</span>
        </div></>}
      </Card>
    );
  };

  // Visible, non-clickable teaser — no onClick/Link anywhere on the card, on
  // purpose: the campaign isn't joinable yet, only announced.
  const renderScheduledCard = (campaign: ScheduledCampaign) => {
    const primaryChannel = campaign.youtube_channels?.[0] ?? null;
    const channelName = campaign.name || primaryChannel?.name || '';
    const channelThumbnail = resolveCampaignAvatar(campaign).src;

    return (
      <Card
        key={campaign.id}
        className="group/card relative overflow-hidden flex flex-col gap-3 p-4 border-amber-500/30 bg-amber-500/[0.03] cursor-default"
      >
        <CampaignBanner src={campaign.banner_url} className="-mx-4 -mt-4 aspect-[2/1]">
          <CampaignBannerInfo categories={campaign.categories} />
        </CampaignBanner>

        <span className="inline-flex items-center gap-1.5 w-fit px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-500 text-[10px] font-bold uppercase tracking-wider">
          <Clock className="w-2.5 h-2.5" />
          {t('earnPage.scheduledBadge')}
        </span>

        <div className="flex items-start gap-3 min-w-0">
          {channelThumbnail ? (
            <img
              src={channelThumbnail}
              alt={channelName}
              className="w-10 h-10 rounded-full object-cover border-2 border-amber-500/30 flex-shrink-0"
            />
          ) : (
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold border-2 bg-amber-500/10 border-amber-500/30 text-amber-500 flex-shrink-0">
              {campaignInitials(channelName)}
            </div>
          )}
          <p className="font-bold text-sm leading-snug pt-1.5">{campaign.name}</p>
        </div>

        <div className="empty:hidden group-has-[[data-campaign-banner]]/card:hidden">
          <CampaignCategoryBadges categories={campaign.categories} compact />
        </div>

        {campaign.campaign_type === 'ranking' ? <RankingCampaignSummary config={campaign.ranking_config} currency={campaign.currency} /> : <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">{t('earnPage.scheduledBudgetLabel')}</span>
          <span className="font-semibold text-foreground">
            {formatCents(campaign.budget_total_cents, campaign.currency)}
          </span>
        </div>}

        <p className="mt-auto text-xs font-semibold text-amber-500">
          {hasScheduledStartDate(campaign.start_date)
            ? t('earnPage.scheduledStartsAt', { date: formatDateTime(campaign.start_date) })
            : t('earnPage.scheduledStartsSoon')}
        </p>
      </Card>
    );
  };

  return (
    <Layout>
    <div className="min-h-screen bg-background p-4 lg:p-8">
      <div className="max-w-5xl mx-auto space-y-12">

        {/* Hero */}
        <div className="text-center space-y-4 pt-4">
          {activeCampaignCount > 0 && (
            <div className="flex justify-center">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/15 border border-primary/30 text-primary text-sm font-semibold">
                <Flame className="w-3.5 h-3.5" />
                {t('earnPage.activeBadge', { count: activeCampaignCount })}
              </span>
            </div>
          )}
          <h1 className="text-4xl sm:text-5xl font-extrabold leading-tight">
            {t('earnPage.heroTitle')}{' '}
            <span className="bg-gradient-primary bg-clip-text text-transparent">
              {t('earnPage.heroTitleHighlight')}
            </span>
          </h1>
          <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto">
            {t('earnPage.heroSubtitle')}
          </p>
        </div>

        {/* Clip&Pix divider com partículas flutuantes */}
        <div className="flex items-center gap-4">
          {/* Lado esquerdo — ícones de clipe sobindo */}
          <div className="relative flex-1 h-14">
            <div className="absolute inset-x-0 top-1/2 h-px bg-gradient-to-r from-transparent to-primary/25" />
            {LEFT_PARTICLES.map(({ Icon, delay, left }, i) => (
              <Icon
                key={i}
                aria-hidden
                className="float-particle absolute w-4 h-4 text-primary/35"
                style={{ left, top: '50%', animationDelay: `${delay}s` }}
              />
            ))}
          </div>

          <img
            src="/images/clip-and-pix-logo.png"
            alt="Clip&Pix"
            className="w-20 opacity-40 select-none flex-shrink-0"
          />

          {/* Lado direito — ícones de dinheiro subindo */}
          <div className="relative flex-1 h-14">
            <div className="absolute inset-x-0 top-1/2 h-px bg-gradient-to-l from-transparent to-primary/25" />
            {RIGHT_PARTICLES.map(({ Icon, delay, left }, i) => (
              <Icon
                key={i}
                aria-hidden
                className="float-particle absolute w-4 h-4 text-primary/35"
                style={{ left, top: '50%', animationDelay: `${delay}s` }}
              />
            ))}
          </div>
        </div>

        {/* Carteira — o clipador vê o que já rendeu antes das campanhas */}
        <CampaignWalletCard />

        {/* Minhas campanhas — as que o clipador já entrou vêm primeiro */}
        {!loading && joinedCampaigns.length > 0 && (
          <div className="space-y-4" data-testid="earn-joined-campaigns">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Flame className="w-5 h-5 text-primary" />
                {t('earnPage.myCampaignsTitle')}
              </h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                {t('earnPage.myCampaignsSubtitle', { count: joinedCampaigns.length })}
              </p>
            </div>
            <div data-testid="earn-joined-grid" className={CAMPAIGN_GRID_CLASS}>
              {joinedCampaigns.map(renderCard)}
            </div>
          </div>
        )}

        {/* Campanhas ativas — as que o clipador ainda não entrou. Quando ele já
            entrou em todas, a seção some por inteiro em vez de deixar um
            cabeçalho sem cartões. */}
        {(loading || internalCampaigns.length > 0 || joinedCampaigns.length === 0) && (
        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-primary" />
              {joinedCampaigns.length > 0
                ? t('earnPage.otherCampaignsTitle')
                : t('earnPage.campaignsTitle')}
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">{t('earnPage.campaignsSubtitle')}</p>
          </div>

          {loading && (
            <div className={CAMPAIGN_GRID_CLASS}>
              {[1, 2, 3, 4].map((i) => (
                <Card key={i} className="p-5 space-y-4 animate-pulse">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-muted" />
                    <div className="space-y-1.5 flex-1">
                      <div className="h-4 bg-muted rounded w-3/4" />
                      <div className="h-3 bg-muted rounded w-1/2" />
                    </div>
                  </div>
                  <div className="h-8 bg-muted rounded w-2/3" />
                  <div className="h-2 bg-muted rounded" />
                  <div className="h-9 bg-muted rounded" />
                </Card>
              ))}
            </div>
          )}

          {!loading && campaigns.length === 0 && (
            <Card className="p-8 text-center border-border/50">
              <Flame className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground text-sm">{t('earnPage.noCampaigns')}</p>
            </Card>
          )}

          {!loading && internalCampaigns.length > 0 && (
            <div data-testid="earn-open-grid" className={CAMPAIGN_GRID_CLASS}>
              {internalCampaigns.map(renderCard)}
            </div>
          )}
        </div>
        )}

        {/* Hall da fama da temporada; o ranking dos clipadores fica atrás de um
            toggle, fechado por padrão. */}
        <CurrentSeasonHall />

        <CollapsibleCpmLeaderboard />

        {/* Como Funciona */}
        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-bold">{t('earnPage.howItWorksTitle')}</h2>
            <p className="text-sm text-muted-foreground mt-0.5">{t('earnPage.howItWorksSubtitle')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {STEPS.map((step, idx) => (
              <div key={step.number} className={`relative${idx === STEPS.length - 1 ? ' sm:col-span-2 lg:col-span-1' : ''}`}>
                <Card className="p-4 space-y-3 h-full border-border/50 bg-card">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex-shrink-0">
                      {step.number}
                    </span>
                    <step.icon className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">{t(step.titleKey)}</p>
                    <p className="text-xs text-muted-foreground mt-1">{t(step.descKey)}</p>
                  </div>
                </Card>
                {idx < STEPS.length - 1 && (
                  <div className="hidden lg:flex absolute -right-2 top-1/2 -translate-y-1/2 z-10 text-muted-foreground/40">
                    <ChevronRight className="w-4 h-4" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Barra de informações de pagamento */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 px-5 py-4 rounded-xl border border-border/60 bg-card/50">
          <div className="flex items-center gap-2 text-sm">
            <ClipboardCheck className="w-4 h-4 text-primary flex-shrink-0" />
            <span className="text-muted-foreground">
              {t('earnPage.paymentBar.approval72h')}{' '}
              <strong className="text-foreground">{t('earnPage.paymentBar.approval72hBold')}</strong>
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4 text-primary flex-shrink-0" />
            <span className="text-muted-foreground">
              {t('earnPage.paymentBar.views72h')}{' '}
              <strong className="text-foreground">{t('earnPage.paymentBar.views72hBold')}</strong>
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <ShieldCheck className="w-4 h-4 text-primary flex-shrink-0" />
            <span className="text-muted-foreground">
              {t('earnPage.paymentBar.payment48h')}{' '}
              <strong className="text-foreground">{t('earnPage.paymentBar.payment48hBold')}</strong>
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Wallet className="w-4 h-4 text-primary flex-shrink-0" />
            <span className="text-muted-foreground">
              {t('earnPage.paymentBar.minWithdrawal')}{' '}
              <strong className="text-foreground">{t('earnPage.paymentBar.minWithdrawalValue')}</strong>
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="ml-auto text-xs gap-1.5"
            onClick={() => faqRef.current?.scrollIntoView({ behavior: 'smooth' })}
          >
            <HelpCircle className="w-3.5 h-3.5" />
            {t('earnPage.paymentBar.viewDetails')}
          </Button>
        </div>

        {/* Em breve — campanhas já confirmadas, ainda não abertas pra participação */}
        {scheduledCampaigns.length > 0 && (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                {t('earnPage.scheduledCampaignsTitle')}
              </h2>
              <p className="text-sm text-muted-foreground mt-0.5">{t('earnPage.scheduledCampaignsSubtitle')}</p>
            </div>
            <div data-testid="earn-scheduled-grid" className={CAMPAIGN_GRID_CLASS}>
              {scheduledCampaigns.map(renderScheduledCard)}
            </div>
          </div>
        )}

        {/* Campanhas encerradas — prova social */}
        {!endedLoading && endedCampaigns.length > 0 && (
          <div className="space-y-4">
            <EndedCampaignsSummary campaigns={endedCampaigns} />
            <div data-testid="earn-ended-grid" className={CAMPAIGN_GRID_CLASS}>
              {endedCampaigns.map(renderEndedCard)}
            </div>
          </div>
        )}

        {/* Vitrine de Parcerias */}
        {MOCK_PARTNERS.length > 0 && (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Handshake className="w-5 h-5 text-primary" />
                {t('earnPage.vitrine.title')}
              </h2>
              <p className="text-sm text-muted-foreground mt-0.5">{t('earnPage.vitrine.subtitle')}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {MOCK_PARTNERS.map((partner) => {
                const initials = partner.name
                  .split(/\s+/)
                  .map((w) => w.charAt(0))
                  .join('')
                  .toUpperCase()
                  .slice(0, 2);

                const badgeLabel = partner.link_type === 'external'
                  ? t('earnPage.vitrine.badgeExternal')
                  : t('earnPage.vitrine.badgeAcervo');

                const ctaLabel = partner.link_type === 'external'
                  ? t('earnPage.vitrine.ctaExternal')
                  : t('earnPage.vitrine.ctaAcervo');

                const acervoUrl = `/community-library?channelId=${partner.channel_id}`;

                return (
                  <Card
                    key={partner.id}
                    className="relative overflow-hidden p-5 space-y-4 border-primary/30 bg-gradient-to-b from-primary/5 via-card to-card hover:shadow-[0_0_20px_rgba(16,185,129,0.15)] transition-shadow"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0">
                        {partner.logo_url ? (
                          <img
                            src={partner.logo_url}
                            alt={partner.name}
                            className="w-12 h-12 rounded-full object-cover border-2 border-primary/40"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold border-2 bg-primary/20 border-primary/30 text-primary">
                            {initials}
                          </div>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="font-bold text-sm truncate">{partner.name}</p>
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-primary/15 text-primary border border-primary/30 flex-shrink-0">
                            {badgeLabel}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">{t('earnPage.vitrine.officialPartner')}</p>
                      </div>
                    </div>

                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {partner.tagline}
                    </p>

                    {partner.link_type === 'external' ? (
                      <Button
                        asChild
                        variant="outline"
                        className="w-full font-bold border-primary/30 text-primary gap-2 hover:bg-primary/10 hover:text-primary"
                      >
                        <a href={partner.external_url!} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-4 h-4" />
                          {ctaLabel}
                        </a>
                      </Button>
                    ) : (
                      <Button
                        asChild
                        variant="outline"
                        className="w-full font-bold border-primary/30 text-primary gap-2 hover:bg-primary/10 hover:text-primary"
                      >
                        <RouterLink to={acervoUrl}>
                          <LibraryBig className="w-4 h-4" />
                          {ctaLabel}
                        </RouterLink>
                      </Button>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Parceiros CPM */}
        {!loading && partnerCampaigns.length > 0 && (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Crown className="w-5 h-5 text-primary" />
                {t('earnPage.partnersTitle')}
              </h2>
              <p className="text-sm text-muted-foreground mt-0.5">{t('earnPage.partnersSubtitle')}</p>
            </div>
            <div className={CAMPAIGN_GRID_CLASS}>
              {partnerCampaigns.map(renderCard)}
            </div>
          </div>
        )}

        {/* FAQ de pagamento */}
        <div ref={faqRef} id="payment-faq" className="space-y-4 pb-4">
          <div className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-bold">{t('earnPage.faq.title')}</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-2">
            {FAQ_KEYS.map((key) => (
              <AccordionItem
                key={key}
                value={key}
                className="border border-border/50 rounded-lg px-4 bg-card/50"
              >
                <AccordionTrigger className="text-sm font-medium text-left hover:no-underline">
                  {t(`earnPage.faq.${key}` as const)}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground">
                  {t(`earnPage.faq.a${key.slice(1)}` as const)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

      </div>
    </div>
    <ClipperFeatureNagModal
      open={isClipperFeatureNagOpen}
      destination="earn"
      onContinue={handleClipperFeatureNagContinue}
    />
    </Layout>
  );
};

export default EarnPage;
